import { Button, Form, Input, Radio, Select, Checkbox, Tooltip, Space } from "antd";
import { Country, State } from "country-state-city";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { addTaxManagement } from "redux/actions";
import { TYPE_OF_THE_TAX } from "shared/constants/AppEnums";
import { InfoCircleOutlined } from "@ant-design/icons";

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const CountryList = Country.getAllCountries().map((data) => {
  return {
    name: data.name,
    countryCode: data.isoCode,
  };
});

const StateList = (countryCode) =>
  State.getStatesOfCountry(countryCode).map((data) => {
    return {
      name: data.name,
      stateCode: data.isoCode,
    };
  });

function AddTaxManagement({ closeModel }) {
  const dispatch = useDispatch();
  const formRef = useRef();

  const [form] = Form.useForm();
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState([]); // now an array for multiple state codes
  const [applyToAllStates, setApplyToAllStates] = useState(false);

  const [taxType, setTaxType] = useState("");
  const [countries, setCountries] = useState(CountryList);

  useEffect(() => {
    // Get raw states for the selected country
    const rawStates = State.getStatesOfCountry(selectedCountry);
    console.log("Raw states for country:", selectedCountry, rawStates); 

    // Map raw states to the structure { name, stateCode } using your StateList logic
    const mappedStates = (rawStates || []).map((data) => ({
      name: data.name,
      stateCode: data.isoCode,
    }));
    setStates(mappedStates);
    setSelectedState([]);
    setApplyToAllStates(false);
  }, [selectedCountry]);

  const onFinish = (values) => {
    console.log("Form values (onFinish):", values);

    let selectedStateCodes;
    
    // If apply to all states is checked, use wildcard "*" to indicate all states
    if (applyToAllStates) {
      selectedStateCodes = ["*"];
    } else {
      // Map selected state names to state codes
      selectedStateCodes = (values.stateCodes || []).map(
        (stateName) => {
          const stateObj = states.find(item => item.name === stateName);
          return stateObj ? stateObj.stateCode : null;
        }
      ).filter(Boolean);
    }

    let payload = {
      ...values,
      countryCode: selectedCountry,
      stateCodes: selectedStateCodes,
    };

    console.log("Payload to send:", payload);
    dispatch(addTaxManagement(payload));
    closeModel();
    form.resetFields();
  };

  const handleSelectChange = (value) => {
    setTaxType(value);
  };

  const onFinishFailed = (errorInfo) => {
    const errorFields = errorInfo.errorFields;
    if (errorFields.length > 0) {
      form.scrollToField(errorFields[0].name[0]);
    }
  };

  return (
    <Form
      className="edit-customer-form"
      name="validate_other"
      layout="vertical"
      {...formItemLayout}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      form={form}
      ref={formRef}
      initialValues={{
        taxName: "",
        countryCode: "",
        stateCodes: [],
        tarifPercentage: "",
        shipping: "",
        kindOfTax: "",
      }}
    >
      <Form.Item
        name="taxName"
        label="Name Of Tax"
        labelWrap
        rules={[
          {
            required: true,
            whitespace: true,
            message: "Tax Name is required",
            type: "string",
          },
        ]}
      >
        <Input placeholder="Enter Tax Name" />
      </Form.Item>
      <Form.Item
        name="taxName_es"
        labelWrap
        label="Nombre del impuesto"
        rules={[
          {
            required: true,
            whitespace: true,
            message: "El nombre fiscal es obligatorio",
            type: "string",
          },
        ]}
      >
        <Input placeholder="Ingrese el nombre del impuesto en español" />
      </Form.Item>
      <Form.Item
        name="countryCode"
        labelWrap
        label="Country"
        rules={[
          {
            required: true,
            whitespace: true,
            message: "Country is required",
          },
        ]}
      >
        <Select
          allowClear
          onChange={(val) => {
            setSelectedCountry(val);
            setSelectedState([]); // reset states on country change
            formRef.current?.setFieldsValue({ stateCodes: [] });
          }}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={countries?.map((item) => ({
            value: item.countryCode,
            label: item.name,
          }))}
        />
      </Form.Item>
      <Form.Item 
        label={
          <Space>
            State/Province
            <Tooltip title="Select specific states or apply tax to the entire country">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        } 
        labelWrap 
        name="stateCodes"
      >
        <div style={{ marginBottom: "10px" }}>
          <Checkbox
            checked={applyToAllStates}
            onChange={(e) => {
              setApplyToAllStates(e.target.checked);
              if (e.target.checked) {
                // If applying to all states, disable state selection
                setSelectedState([]);
                formRef.current?.setFieldsValue({ stateCodes: [] });
              }
            }}
          >
            Apply to entire country (all states/provinces)
          </Checkbox>
        </div>
        
        {!applyToAllStates && (
          <>
            <Select
              mode="multiple"
              showSearch
              style={{ width: '100%' }}
              placeholder="Select specific states/provinces"
              value={selectedState}
              disabled={applyToAllStates}
              onChange={(val) => {
                console.log("val", val);
                setSelectedState(val);
                formRef.current?.setFieldsValue({ stateCodes: val });
              }}
              filterOption={(inputValue, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
              options={states?.map((item) => ({
                value: item.name,
                label: item.name,
              }))}
            />
          </>
        )}
      </Form.Item>
      <Form.Item
        name="tarifPercentage"
        labelWrap
        label="Tax Percentage"
        rules={[
          {
            required: true,
            message: "Please enter tax percentage",
            type: "string",
          },
        ]}
      >
        <Input placeholder="Enter Tax Percentage" />
      </Form.Item>

      <Form.Item name="shipping" labelWrap label="isShipping">
        <Radio.Group name="shipping">
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label="Kind OF Tax"
        name="kindOfTax"
        labelWrap
        rules={[{ required: true, message: "Please Select Tax Type" }]}
      >
        <Select
          placeholder="Select Tax Type"
          allowClear
          optionFilterProp="children"
          onChange={handleSelectChange}
        >
          {TYPE_OF_THE_TAX?.map((ct) => {
            return <Select.Option key={ct.value}>{ct.name}</Select.Option>;
          })}
        </Select>
      </Form.Item>
      <Form.Item
        wrapperCol={{
          span: 12,
          offset: 10,
        }}
        labelWrap
      >
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
}

export default AddTaxManagement;

AddTaxManagement.propTypes = {
  closeModel: PropTypes.func,
};
